import axios, { AxiosInstance } from 'axios';

// Types matching the backend
export interface DemoUser {
  id: string;
  name: string;
  email: string;
  role: 'LawEnforcement' | 'Attorney' | 'PublicSubmitter' | 'CaseManager' | 'SystemAdmin' | 'Judge';
  department: string;
  badgeNumber?: string;
  barNumber?: string;
  isActive: boolean;
}

export interface Evidence {
  id: string;
  caseNumber: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  sha256Hash: string;
  uploadedBy: string;
  uploadedAt: string;
  description: string;
  evidenceType: string;
  containerName: string;
  blobPath: string;
  integrityStatus: 'verified' | 'failed' | 'pending';
  legalHold: boolean;
  retentionExpiresAt: string;
  // Demo mode fields for external content
  externalUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  evidenceStatus?: 'Active' | 'PendingReview' | 'Retracted' | 'Rejected';
  // AI processing status (populated after Content Understanding runs)
  AiExtracted?: boolean;
  AiIndexedAt?: string;
}

export interface CustodyChainEvent {
  id: string;
  evidenceId: string;
  eventType: 'upload' | 'download' | 'view' | 'transfer' | 'legal_hold' | 'integrity_check';
  performedBy: string;
  performedByName: string;
  performedAt: string;
  description: string;
  ipAddress: string;
  userAgent: string;
  previousHash?: string;
  newHash?: string;
  metadata?: Record<string, unknown>;
}

export interface Case {
  caseNumber: string;
  title: string;
  description: string;
  status: 'open' | 'closed' | 'pending' | 'archived' | 'Active' | 'Pending Review';
  createdAt: string;
  assignedTo: string;
  jurisdiction: string;
  evidenceCount?: number;
  court?: string;
  assignedJudge?: string;
  type?: string;
  leadInvestigator?: string;
  prosecutingAttorney?: string;
  defenseAttorney?: string;
  filingDate?: string;
  // Resolved display names from DemoUsers JOIN
  assignedJudgeName?: string;
  leadInvestigatorName?: string;
  prosecutingAttorneyName?: string;
  defenseAttorneyName?: string;
}

export interface CourtFiling {
  id: number;
  caseNumber: string;
  filingType: string;
  title: string;
  description?: string;
  filedBy: string;
  filedByRole?: string;
  filedAt: string;
  status: 'Pending' | 'Accepted' | 'Rejected';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  attachmentFileName?: string;
  attachmentFileSize?: number;
  // Joined fields
  caseTitle?: string;
  caseType?: string;
}

export interface Annotation {
  Id: string;
  EvidenceId: string;
  UserId: string;
  UserName: string;
  AnnotationType: 'highlight' | 'rectangle' | 'freehand' | 'text' | 'arrow' | 'redaction';
  PageNumber: number | null;
  Coordinates: string;
  Content: string | null;
  Color: string;
  Visibility: 'private' | 'case-team' | 'courtroom';
  CreatedAt: string;
}

export interface AuthResponse {
  token: string;
  user: DemoUser;
  expiresAt: string;
}

export interface CustodyStats {
  totalEvents: number;
  byEventType: Record<string, number>;
  recentActivity: CustodyChainEvent[];
}

// API base URL - auto-detect in production, use env var or /api for local dev
function getApiBaseUrl(): string {
  // Check for explicit environment variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Auto-detect Azure Static Web Apps environment
  const hostname = window.location.hostname;
  if (hostname.includes('azurestaticapps.net') || hostname.includes('dems')) {
    // In Azure, use the deployed Function App
    return 'https://func-dems-api-dev.azurewebsites.net/api';
  }
  
  // Local development
  return '/api';
}

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.token = null;
          localStorage.removeItem('dems_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Restore token from localStorage
    const savedToken = localStorage.getItem('dems_token');
    if (savedToken) {
      this.token = savedToken;
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('dems_token', token);
    } else {
      localStorage.removeItem('dems_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  // Auth endpoints
  async demoLogin(pin: string, userId?: string): Promise<AuthResponse> {
    const response = await this.client.post<{ success: boolean; data: { token: string; user: DemoUser; expiresIn: string } }>('/auth/demo-login', { pin, userId });
    const { token, user, expiresIn } = response.data.data;
    this.setToken(token);
    // Convert expiresIn to expiresAt timestamp
    const hours = parseInt(expiresIn) || 8;
    const expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    return { token, user, expiresAt };
  }

  async getCurrentUser(): Promise<DemoUser> {
    const response = await this.client.get<{ success: boolean; data: DemoUser }>('/auth/me');
    return response.data.data;
  }

  async getDemoUsers(): Promise<Omit<DemoUser, 'pin'>[]> {
    const response = await this.client.get<{ success: boolean; data: Omit<DemoUser, 'pin'>[] }>('/auth/demo-users');
    return response.data.data;
  }

  logout() {
    this.setToken(null);
  }

  // Map backend case fields to frontend format
  private mapBackendCase(c: any): Case {
    return {
      caseNumber: c.CaseNumber || c.caseNumber,
      title: c.Title || c.title,
      description: c.Description || c.description || '',
      status: c.Status || c.status || 'open',
      createdAt: c.FilingDate || c.filingDate || c.createdAt || new Date().toISOString(),
      assignedTo: c.AssignedJudge || c.assignedJudge || c.assignedTo || '',
      jurisdiction: c.Court || c.court || c.jurisdiction || '',
      evidenceCount: c.EvidenceCount || c.evidenceCount || 0,
      court: c.Court || c.court,
      assignedJudge: c.AssignedJudge || c.assignedJudge,
      type: c.Type || c.type,
      leadInvestigator: c.LeadInvestigator || c.leadInvestigator,
      prosecutingAttorney: c.ProsecutingAttorney || c.prosecutingAttorney,
      defenseAttorney: c.DefenseAttorney || c.defenseAttorney,
      assignedJudgeName: c.AssignedJudgeName || c.assignedJudgeName,
      leadInvestigatorName: c.LeadInvestigatorName || c.leadInvestigatorName,
      prosecutingAttorneyName: c.ProsecutingAttorneyName || c.prosecutingAttorneyName,
      defenseAttorneyName: c.DefenseAttorneyName || c.defenseAttorneyName,
    };
  }

  // Cases endpoints
  async getCases(): Promise<Case[]> {
    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>('/cases');
    const cases = response.data.data || [];
    return cases.map(this.mapBackendCase.bind(this));
  }

  async getCase(caseNumber: string): Promise<Case> {
    const response = await this.client.get<{ success: boolean; data: any }>(`/cases/${caseNumber}`);
    return this.mapBackendCase(response.data.data);
  }

  async createCase(data: { title: string; type: 'Criminal' | 'Civil'; description?: string; court?: string }): Promise<Case> {
    const response = await this.client.post<{ success: boolean; data: any }>('/cases', data);
    return this.mapBackendCase(response.data.data);
  }

  async searchPublicCases(q: string): Promise<{ caseNumber: string; title: string; court: string; status: string }[]> {
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/cases/public-search?q=${encodeURIComponent(q)}`);
    return (response.data.data || []).map(c => ({
      caseNumber: c.CaseNumber || c.caseNumber,
      title: c.Title || c.title,
      court: c.Court || c.court || '',
      status: c.Status || c.status || 'Active',
    }));
  }

  async updateCaseStatus(caseNumber: string, status: string): Promise<void> {
    await this.client.patch(`/cases/${encodeURIComponent(caseNumber)}/status`, { status });
  }

  async updateCaseTeam(caseNumber: string, team: {
    assignedJudge?: string | null;
    leadInvestigator?: string | null;
    prosecutingAttorney?: string | null;
    defenseAttorney?: string | null;
  }): Promise<void> {
    await this.client.patch(`/cases/${encodeURIComponent(caseNumber)}/team`, team);
  }

  // User case registrations (for PublicSubmitter "My Cases")
  async getUserCases(): Promise<Case[]> {
    const response = await this.client.get<{ success: boolean; data: any[] }>('/users/me/cases');
    return (response.data.data || []).map(this.mapBackendCase);
  }

  async registerToCase(caseNumber: string): Promise<void> {
    await this.client.post(`/users/me/cases/${encodeURIComponent(caseNumber)}`);
  }

  async unregisterFromCase(caseNumber: string): Promise<void> {
    await this.client.delete(`/users/me/cases/${encodeURIComponent(caseNumber)}`);
  }

  // Evidence endpoints
  async getCaseEvidence(caseNumber: string): Promise<Evidence[]> {
    const response = await this.client.get<{ success: boolean; data: Evidence[]; total: number }>(`/cases/${caseNumber}/evidence`);
    // Map backend field names to frontend field names
    const evidenceList = response.data.data || [];
    const mapped = evidenceList.map(this.mapBackendEvidence);
    // Append demo-only items that exist in seed data but not the live database
    const extras = this.getDemoExtraEvidence(caseNumber);
    const existingIds = new Set(mapped.map((e) => e.id));
    return [...mapped, ...extras.filter((e) => !existingIds.has(e.id))];
  }

  async getEvidence(evidenceId: string): Promise<Evidence> {
    try {
      const response = await this.client.get<{ success: boolean; data: Evidence }>(`/evidence/${evidenceId}`);
      return this.mapBackendEvidence(response.data.data);
    } catch {
      // Fall back to demo data for seed-only evidence items
      const demoItem = this.getDemoExtraEvidence(null).find((e) => e.id === evidenceId);
      if (demoItem) return demoItem;
      throw new Error(`Evidence ${evidenceId} not found`);
    }
  }

  private getDemoExtraEvidence(caseNumber: string | null): Evidence[] {
    const items: Evidence[] = [
      {
        id: 'EVD-2026-0017',
        caseNumber: '2026-CR-0042',
        fileName: 'southern_cross_station_vicinity.jpg',
        fileType: 'image/jpeg',
        fileSize: 3981312,
        sha256Hash: 'e3f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4',
        uploadedBy: 'james.chen@demo.dems',
        uploadedAt: '2026-03-12T11:20:00Z',
        description: 'Photograph taken at Southern Cross Station, Melbourne. Shows the tram stop and surrounding street area where the subject was last tracked.',
        evidenceType: 'Image',
        containerName: '',
        blobPath: '',
        integrityStatus: 'verified',
        legalHold: false,
        retentionExpiresAt: '',
        externalUrl: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=1920',
        thumbnailUrl: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=300',
        title: 'Incident Vicinity - Southern Cross Station',
        evidenceStatus: 'Active',
      },
      {
        id: 'EVD-2026-0018',
        caseNumber: '2026-CR-0042',
        fileName: '15519-865412862.mp4',
        fileType: 'video/mp4',
        fileSize: 8388608,
        sha256Hash: 'f4a5b6c7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5',
        uploadedBy: 'james.chen@demo.dems',
        uploadedAt: '2026-03-12T11:22:00Z',
        description: '19-second clip from fixed street surveillance camera near Southern Cross Station. Captured tram 6004 (Route 122) and Silver Top Taxi #9966 near the incident location.',
        evidenceType: 'Video',
        containerName: '',
        blobPath: '',
        integrityStatus: 'verified',
        legalHold: false,
        retentionExpiresAt: '',
        externalUrl: 'https://videos.pexels.com/video-files/2053100/2053100-hd_1920_1080_30fps.mp4',
        thumbnailUrl: 'https://images.pexels.com/videos/2053100/free-video-2053100.jpg?auto=compress&w=300',
        title: 'Street Surveillance - Southern Cross Station (19s)',
        evidenceStatus: 'Active',
      },
    ];
    if (caseNumber === null) return items;
    return items.filter((e) => e.caseNumber === caseNumber);
  }

  // Map backend field names (PascalCase/snake_case) to frontend field names (camelCase)
  private mapBackendEvidence(e: any): Evidence {
    // Parse Metadata JSON to extract externalUrl/thumbnailUrl when not separate columns
    let metadata: Record<string, string> = {};
    try {
      const raw = e.Metadata || e.metadata;
      if (raw) metadata = JSON.parse(raw);
    } catch { /* ignore malformed metadata */ }

    return {
      id: e.Id || e.id,
      caseNumber: e.CaseNumber || e.caseNumber,
      fileName: e.FileName || e.fileName,
      fileType: e.MimeType || e.mimeType || e.fileType,
      fileSize: e.FileSize || e.fileSize,
      sha256Hash: e.Sha256Hash || e.sha256Hash,
      uploadedBy: e.UploadedBy || e.uploadedBy,
      uploadedAt: e.UploadedAt || e.uploadedAt,
      description: e.Description || e.description || '',
      evidenceType: e.Type || e.Category || e.evidenceType || 'Document',
      containerName: e.containerName || '',
      blobPath: e.BlobPath || e.blobPath || '',
      integrityStatus: e.integrityStatus || 'verified',
      legalHold: e.legalHold || false,
      retentionExpiresAt: e.retentionExpiresAt || '',
      // ExternalUrl/ThumbnailUrl may be dedicated columns (from JSON_VALUE query) or in Metadata JSON
      externalUrl: e.ExternalUrl || e.externalUrl || metadata.externalUrl,
      thumbnailUrl: e.ThumbnailUrl || e.thumbnailUrl || metadata.thumbnailUrl,
      title: e.Title || e.title || e.fileName,
      evidenceStatus: e.EvidenceStatus || e.evidenceStatus || 'Active',
      AiExtracted: e.AiExtracted === true || (e.AiExtracted as unknown) === 1
        ? true
        : e.AiExtracted === false || (e.AiExtracted as unknown) === 0
        ? false
        : undefined,
      AiIndexedAt: e.AiIndexedAt || e.aiIndexedAt || metadata.extractedAt,
    };
  }

  async uploadEvidence(
    caseNumber: string,
    file: File,
    metadata: {
      description: string;
      evidenceType: string;
    },
    onProgress?: (progress: number) => void
  ): Promise<Evidence> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caseNumber', caseNumber);
    formData.append('title', file.name);
    formData.append('description', metadata.description);
    formData.append('type', metadata.evidenceType);

    const response = await this.client.post<{ success: boolean; data: { id: string; caseNumber: string; title: string; sha256Hash: string; custodyEventId: string } }>(
      '/evidence/upload',
      formData,
      {
        // Set Content-Type to undefined to override the axios default ('application/json')
        // and let Axios auto-detect multipart/form-data with the correct boundary.
        headers: {
          'Content-Type': undefined,
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    // Return as Evidence object
    const data = response.data.data;
    return {
      id: data.id,
      caseNumber: data.caseNumber,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      sha256Hash: data.sha256Hash,
      uploadedBy: '',
      uploadedAt: new Date().toISOString(),
      description: metadata.description,
      evidenceType: metadata.evidenceType,
      containerName: '',
      blobPath: '',
      integrityStatus: 'verified',
      legalHold: false,
      retentionExpiresAt: '',
    };
  }

  // Evidence intake workflow: retract, accept, reject
  async retractEvidence(evidenceId: string): Promise<void> {
    await this.client.patch(`/evidence/${evidenceId}/retract`);
  }

  async acceptEvidence(evidenceId: string): Promise<void> {
    await this.client.patch(`/evidence/${evidenceId}/accept`);
  }

  async rejectEvidence(evidenceId: string, reason: string): Promise<void> {
    await this.client.patch(`/evidence/${evidenceId}/reject`, { reason });
  }

  async getPendingEvidence(): Promise<Evidence[]> {
    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>('/pending-evidence');
    return (response.data.data || []).map(this.mapBackendEvidence);
  }

  // Annotation endpoints
  async getAnnotations(evidenceId: string): Promise<Annotation[]> {
    const response = await this.client.get<{ success: boolean; data: Annotation[] }>(`/evidence/${evidenceId}/annotations`);
    return response.data.data || [];
  }

  async createAnnotation(evidenceId: string, annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
    visibility?: Annotation['Visibility'];
  }): Promise<Annotation> {
    const response = await this.client.post<{ success: boolean; data: Annotation }>(`/evidence/${evidenceId}/annotations`, annotation);
    return response.data.data;
  }

  async deleteAnnotation(evidenceId: string, annotationId: string): Promise<void> {
    await this.client.delete(`/evidence/${evidenceId}/annotations/${annotationId}`);
  }

  // Redaction endpoints
  async finalizeRedactions(evidenceId: string): Promise<{
    redactedEvidenceId: string;
    sourceEvidenceId: string;
    redactionCount: number;
    redactedFileName: string;
    redactionApplied: boolean;
    message: string;
  }> {
    const response = await this.client.post<{ success: boolean; data: any }>(`/evidence/${evidenceId}/redactions/finalize`);
    return response.data.data;
  }

  async getRedactionStatus(evidenceId: string): Promise<{
    draftCount: number;
    finalizedCount: number;
    redactedCopies: Array<{ id: string; title: string; fileName: string; createdBy: string; createdAt: string }>;
  }> {
    const response = await this.client.get<{ success: boolean; data: any }>(`/evidence/${evidenceId}/redactions/status`);
    return response.data.data;
  }

  async getPreviewBlobUrl(evidenceId: string): Promise<string> {
    const token = this.getToken();
    const baseUrl = this.client.defaults.baseURL;
    const url = `${baseUrl}/evidence/${evidenceId}/stream`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Preview unavailable');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async downloadEvidence(evidenceId: string): Promise<{ url: string; filename: string }> {
    // The backend returns the actual file, so we need to use a different approach
    // For demo mode with external URLs, we get the evidence first then use its external URL
    const evidence = await this.getEvidence(evidenceId);
    
    if ((evidence as any).externalUrl) {
      // Demo mode: use external URL
      return {
        url: (evidence as any).externalUrl,
        filename: evidence.fileName,
      };
    }
    
    // Real mode: return the download endpoint URL
    // The browser will handle the download via the response headers
    const token = this.getToken();
    const baseUrl = this.client.defaults.baseURL;
    return {
      url: `${baseUrl}/evidence/${evidenceId}/download${token ? `?token=${token}` : ''}`,
      filename: evidence.fileName,
    };
  }

  // Custody chain endpoints
  async getEvidenceCustodyChain(evidenceId: string): Promise<CustodyChainEvent[]> {
    const response = await this.client.get<{ success: boolean; data: { evidenceId: string; events: any[]; totalEvents: number } }>(`/custody/${evidenceId}`);
    // Map backend field names to frontend field names
    const events = response.data.data?.events || [];
    return events.map(this.mapBackendCustodyEvent);
  }

  // Map backend custody event fields to frontend format
  private mapBackendCustodyEvent(e: any): CustodyChainEvent {
    // Map event types: backend uses PascalCase, frontend uses snake_case
    const eventTypeMap: Record<string, string> = {
      'Upload': 'upload',
      'Uploaded': 'upload',
      'Download': 'download',
      'Access': 'view',
      'View': 'view',
      'Share': 'transfer',
      'Transfer': 'transfer',
      'LegalHold': 'legal_hold',
      'Legal_Hold': 'legal_hold',
      'IntegrityCheck': 'integrity_check',
      'Integrity_Check': 'integrity_check',
      'Redact': 'view',
    };
    
    const backendType = e.EventType || e.eventType || 'view';
    const mappedType = eventTypeMap[backendType] || backendType.toLowerCase().replace(/([A-Z])/g, '_$1').toLowerCase();
    
    return {
      id: e.Id || e.id,
      evidenceId: e.EvidenceId || e.evidenceId,
      eventType: mappedType as CustodyChainEvent['eventType'],
      performedBy: e.Actor || e.actor || e.performedBy,
      performedByName: e.ActorName || e.actorName || e.performedByName,
      performedAt: e.Timestamp || e.timestamp || e.performedAt,
      description: e.Description || e.description,
      ipAddress: e.IpAddress || e.ipAddress || '',
      userAgent: e.userAgent || '',
      previousHash: e.previousHash,
      newHash: e.newHash,
      metadata: e.Metadata ? (typeof e.Metadata === 'string' ? JSON.parse(e.Metadata) : e.Metadata) : e.metadata,
    };
  }

  async getCustodyEvents(filters?: { startDate?: string; endDate?: string; eventType?: string }): Promise<CustodyChainEvent[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('from', filters.startDate);
    if (filters?.endDate) params.append('to', filters.endDate);
    if (filters?.eventType) params.append('eventType', filters.eventType);

    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>(`/custody?${params.toString()}`);
    const events = response.data.data || [];
    return events.map(this.mapBackendCustodyEvent);
  }

  async getCustodyStats(): Promise<CustodyStats> {
    const response = await this.client.get<{ success: boolean; data: { eventsByType: Record<string, number>; eventsLast24Hours: number; activeActorsLast7Days: number; totalEvidence: number } }>('/custody/stats');
    const data = response.data.data;
    return {
      totalEvents: Object.values(data.eventsByType || {}).reduce((a, b) => a + b, 0),
      byEventType: data.eventsByType || {},
      recentActivity: [],
    };
  }

  // Log a view event for evidence
  async logViewEvent(evidenceId: string): Promise<void> {
    await this.client.post(`/custody/${evidenceId}/log-view`);
  }

  // Log a download event for evidence
  async logDownloadEvent(evidenceId: string): Promise<void> {
    await this.client.post(`/custody/${evidenceId}/log-download`);
  }

  // Verify evidence integrity on-demand
  async verifyIntegrity(evidenceId: string): Promise<{ 
    verified: boolean; 
    storedHash: string; 
    currentHash?: string; 
    message: string;
  }> {
    try {
      const response = await this.client.post<{ 
        success: boolean; 
        data: { verified: boolean; storedHash: string; currentHash?: string; message: string } 
      }>(`/evidence/${evidenceId}/verify`);
      return response.data.data;
    } catch {
      // If endpoint doesn't exist, return verified based on evidence data
      const evidence = await this.getEvidence(evidenceId);
      return {
        verified: evidence.integrityStatus === 'verified',
        storedHash: evidence.sha256Hash,
        message: evidence.integrityStatus === 'verified' 
          ? 'Hash verified against stored value' 
          : 'Integrity check pending or failed'
      };
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // =============================================
  // EXHIBITS - Court Filing Workflow
  // =============================================

  async getExhibits(caseNumber?: string): Promise<any[]> {
    const params = caseNumber ? `?caseNumber=${encodeURIComponent(caseNumber)}` : '';
    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>(`/exhibits${params}`);
    return response.data.data || [];
  }

  async getExhibit(exhibitId: string): Promise<any> {
    const response = await this.client.get<{ success: boolean; data: any }>(`/exhibits/${exhibitId}`);
    return response.data.data;
  }

  async createExhibit(data: {
    caseNumber: string;
    exhibitNumber: string;
    title: string;
    description?: string;
    evidenceIds?: string[];
  }): Promise<any> {
    const response = await this.client.post<{ success: boolean; data: any }>('/exhibits', data);
    return response.data.data;
  }

  async fileExhibit(exhibitId: string): Promise<any> {
    const response = await this.client.post<{ success: boolean; data: any }>(`/exhibits/${exhibitId}/file`);
    return response.data.data;
  }

  async addEvidenceToExhibit(exhibitId: string, evidenceId: string, notes?: string): Promise<void> {
    await this.client.post(`/exhibits/${exhibitId}/evidence`, { evidenceId, notes });
  }

  // =============================================
  // DISCOVERY - Evidence Sharing
  // =============================================

  async getDiscoveryShares(caseNumber: string): Promise<any[]> {
    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>(`/discovery/shares?caseNumber=${encodeURIComponent(caseNumber)}`);
    return response.data.data || [];
  }

  async createDiscoveryShare(data: {
    caseNumber: string;
    evidenceId?: string;
    sharedWith: string;
    sharedWithRole: string;
    shareType: 'View' | 'Download' | 'Full';
    expiresInDays?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<{ success: boolean; data: any }>('/discovery/shares', data);
    return response.data.data;
  }

  async revokeDiscoveryShare(shareId: string): Promise<void> {
    await this.client.delete(`/discovery/shares/${shareId}`);
  }

  // Get cases where current user is assigned as attorney
  async getAttorneyCases(): Promise<any[]> {
    const response = await this.client.get<{ success: boolean; data: any[]; total: number }>('/discovery/my-cases');
    return response.data.data || [];
  }

  // Get evidence for discovery view (defense sees only shared items)
  async getCaseEvidenceForDiscovery(caseNumber: string): Promise<{ evidence: Evidence[]; role: string }> {
    const response = await this.client.get<{ success: boolean; data: any[]; role: string }>(`/discovery/cases/${encodeURIComponent(caseNumber)}/evidence`);
    return { evidence: (response.data.data || []).map(this.mapBackendEvidence), role: response.data.role || 'Unknown' };
  }

  // Share evidence with defense (prosecution -> defense)
  async shareEvidenceWithDefense(caseNumber: string, evidenceIds: string[], notes?: string): Promise<{ sharedCount: number; defenseAttorney: string }> {
    const response = await this.client.post<{ success: boolean; sharedCount: number; defenseAttorney: string }>('/discovery/share-with-defense', {
      caseNumber,
      evidenceIds,
      notes
    });
    return { sharedCount: response.data.sharedCount, defenseAttorney: response.data.defenseAttorney };
  }

  // =============================================
  // COURT SHARES - Courtroom Evidence Sharing
  // =============================================

  async getCaseParties(caseNumber: string): Promise<{ Email: string; Name: string; Role: string; label: string }[]> {
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/cases/${caseNumber}/parties`);
    return response.data.data || [];
  }

  async createCourtShare(data: {
    caseNumber: string;
    exhibitId?: string;
    evidenceId?: string;
    sharedWithEmail: string;
    message?: string;
  }): Promise<{ id: string; sharedWith: string; sharedWithRole: string }> {
    const response = await this.client.post<{ success: boolean; data: any }>('/courtshares', data);
    return response.data.data;
  }

  async getMyCourtShares(caseNumber?: string): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('my', 'true');
    if (caseNumber) params.append('caseNumber', caseNumber);
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/courtshares?${params}`);
    return response.data.data || [];
  }

  async getCourtShares(caseNumber: string): Promise<any[]> {
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/courtshares?caseNumber=${caseNumber}`);
    return response.data.data || [];
  }

  async acknowledgeCourtShare(shareId: string): Promise<void> {
    await this.client.post(`/courtshares/${shareId}/acknowledge`);
  }

  async dismissCourtShare(shareId: string): Promise<void> {
    await this.client.post(`/courtshares/${shareId}/dismiss`);
  }

  // =============================================
  // COURT FILINGS - Motions, briefs, legal documents
  // =============================================

  async getFilingTypes(): Promise<string[]> {
    const response = await this.client.get<{ success: boolean; data: string[] }>('/court-filings/types');
    return response.data.data || [];
  }

  async getCaseFilings(caseNumber: string): Promise<CourtFiling[]> {
    const response = await this.client.get<{ success: boolean; data: CourtFiling[] }>(`/court-filings/cases/${caseNumber}`);
    return response.data.data || [];
  }

  async getPendingFilings(): Promise<CourtFiling[]> {
    const response = await this.client.get<{ success: boolean; data: CourtFiling[] }>('/court-filings/pending');
    return response.data.data || [];
  }

  async submitFiling(data: { caseNumber: string; filingType: string; title: string; description?: string }): Promise<{ caseNumber: string; filingType: string; status: string }> {
    const response = await this.client.post<{ success: boolean; data: any }>('/court-filings', data);
    return response.data.data;
  }

  async reviewFiling(filingId: number, action: 'accept' | 'reject', notes?: string): Promise<{ filingId: number; status: string }> {
    const response = await this.client.post<{ success: boolean; data: any }>(`/court-filings/${filingId}/review`, { action, notes });
    return response.data.data;
  }

  // =============================================
  // DISPOSITION - Retention, Legal Holds, Disposal
  // =============================================

  async getDispositionDashboard(): Promise<{
    stats: {
      active: number;
      onHold: number;
      pendingDisposal: number;
      archived: number;
      disposed: number;
      activeHolds: number;
    };
    approachingRetention: number;
    recentActivity: any[];
  }> {
    const response = await this.client.get<{ success: boolean; data: any }>('/disposition/dashboard');
    return response.data.data;
  }

  async getDispositionEvidence(params?: { status?: string; caseNumber?: string }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.caseNumber) queryParams.append('caseNumber', params.caseNumber);
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/disposition/evidence?${queryParams}`);
    return response.data.data || [];
  }

  async getLegalHolds(params?: { caseNumber?: string; activeOnly?: boolean }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.caseNumber) queryParams.append('caseNumber', params.caseNumber);
    if (params?.activeOnly) queryParams.append('activeOnly', 'true');
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/disposition/holds?${queryParams}`);
    return response.data.data || [];
  }

  async applyLegalHold(data: {
    caseNumber: string;
    evidenceId?: string;
    holdName: string;
    holdReason: string;
    orderReference?: string;
    expiresAt?: string;
  }): Promise<any> {
    const response = await this.client.post<{ success: boolean; data: any }>('/disposition/holds', data);
    return response.data.data;
  }

  async releaseLegalHold(holdId: string, releaseReason: string): Promise<void> {
    await this.client.post(`/disposition/holds/${holdId}/release`, { releaseReason });
  }

  async setRetentionDate(evidenceId: string, retentionDate: string, reason?: string): Promise<void> {
    await this.client.post(`/disposition/evidence/${evidenceId}/retention`, { retentionDate, reason });
  }

  async archiveEvidence(evidenceId: string, reason?: string): Promise<void> {
    await this.client.post(`/disposition/evidence/${evidenceId}/archive`, { reason });
  }

  async disposeEvidence(evidenceId: string, reason: string): Promise<void> {
    await this.client.post(`/disposition/evidence/${evidenceId}/dispose`, { reason, attestation: true });
  }

  async getRetentionPolicies(): Promise<any[]> {
    const response = await this.client.get<{ success: boolean; data: any[] }>('/disposition/policies');
    return response.data.data || [];
  }

  async getDispositionLog(params?: { evidenceId?: string; caseNumber?: string; action?: string }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.evidenceId) queryParams.append('evidenceId', params.evidenceId);
    if (params?.caseNumber) queryParams.append('caseNumber', params.caseNumber);
    if (params?.action) queryParams.append('action', params.action);
    const response = await this.client.get<{ success: boolean; data: any[] }>(`/disposition/log?${queryParams}`);
    return response.data.data || [];
  }

  // =============================================
  // NOTIFICATIONS
  // =============================================

  /**
   * Get notifications for current user
   */
  async getNotifications(params?: { 
    category?: string; 
    unreadOnly?: boolean;
    limit?: number;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.unreadOnly) queryParams.append('unreadOnly', 'true');
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    try {
      const response = await this.client.get<{ success: boolean; data: any[] }>(`/notifications?${queryParams}`);
      return response.data.data || [];
    } catch {
      // Return demo notifications if endpoint not available
      return this.getDemoNotifications();
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<void> {
    try {
      await this.client.patch(`/notifications/${notificationId}/read`);
    } catch {
      console.log('[API] Mark read - demo mode');
    }
  }

  /**
   * Acknowledge a critical notification
   */
  async acknowledgeNotification(notificationId: string, comment?: string): Promise<void> {
    try {
      await this.client.post(`/notifications/${notificationId}/acknowledge`, { comment });
    } catch {
      console.log('[API] Acknowledge - demo mode');
    }
  }

  /**
   * Get notification count for badge
   */
  async getNotificationCount(): Promise<{ total: number; unread: number; critical: number }> {
    try {
      const response = await this.client.get<{ success: boolean; data: any }>('/notifications/count');
      return response.data.data;
    } catch {
      return { total: 5, unread: 3, critical: 1 };
    }
  }

  /**
   * Get user notification preferences
   */
  async getNotificationPreferences(): Promise<any> {
    try {
      const response = await this.client.get<{ success: boolean; data: any }>('/notifications/preferences');
      return response.data.data;
    } catch {
      return { enabled: true, emailDigest: { enabled: true, frequency: 'Daily' } };
    }
  }

  /**
   * Update notification preferences
   */
  async updateNotificationPreferences(preferences: any): Promise<void> {
    await this.client.put('/notifications/preferences', preferences);
  }

  /**
   * Agent Aegis — document-specific Q&A.
   * Uses the axios client so base URL is resolved correctly in all environments
   * (avoids the raw-fetch + VITE_API_URL pattern that breaks on the SWA).
   */
  async aegisAsk(
    evidenceId: string,
    question: string,
    caseNumber: string
  ): Promise<{ answer: string; source?: any }> {
    const response = await this.client.post<{
      success: boolean;
      answer: string;
      source?: any;
    }>('/aegis/ask', { evidenceId, question, caseNumber });
    if (!response.data.success) {
      throw new Error('Aegis request failed');
    }
    return { answer: response.data.answer, source: response.data.source };
  }

  /**
   * Get AI insights for a video/image evidence item.
   * Uses real CU-extracted data from AI Search index when available;
   * falls back to demo data if the item hasn't been processed yet.
   */
  async getVideoInsights(evidenceId: string): Promise<any> {
    try {
      const response = await this.client.get<{ success: boolean; data: any }>(`/evidence/${evidenceId}/insights`);
      return response.data.data;
    } catch {
      return this.getDemoVideoInsights(evidenceId);
    }
  }

  private getDemoVideoInsights(evidenceId: string): any {
    const isMelbourneClip = evidenceId === 'EVD-2026-0018' || evidenceId === 'EVD-2026-0021';
    const isKidsMissing   = evidenceId === 'EVD-2026-0020';

    if (isKidsMissing) {
      return {
        videoIndexerId: '3tnhn3q0uk',
        viAccountId: 'd824318e-85c2-4c44-a177-caa3861fa1bd',
        viLocation: 'trial',
        processedAt: '2026-03-15T09:22:00Z',
        duration: '1:04:00',
        durationSeconds: 3840,
        scenes: 4,
        shots: 8,
        source: 'video-indexer',
        faces: [],
        observedPeople: [
          { id: 1,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:04:42', adjustedEnd: '0:04:48' }] },
          { id: 2,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:05:10', adjustedEnd: '0:05:15' }] },
          { id: 3,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:06:22', adjustedEnd: '0:06:28' }] },
          { id: 4,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:08:30', adjustedEnd: '0:08:36' }] },
          { id: 5,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:09:45', adjustedEnd: '0:09:51' }] },
          { id: 6,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:13:55', adjustedEnd: '0:14:01' }] },
          { id: 7,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:16:20', adjustedEnd: '0:16:26' }] },
          { id: 8,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:18:44', adjustedEnd: '0:18:50' }] },
          { id: 9,  thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:20:15', adjustedEnd: '0:20:21' }] },
          { id: 10, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:24:38', adjustedEnd: '0:24:44' }] },
          { id: 11, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:28:10', adjustedEnd: '0:28:16' }] },
          { id: 12, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:31:55', adjustedEnd: '0:32:01' }] },
          { id: 13, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:35:42', adjustedEnd: '0:35:48' }] },
          { id: 14, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:40:10', adjustedEnd: '0:40:16' }] },
          { id: 15, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:46:30', adjustedEnd: '0:46:36' }] },
          { id: 16, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } },  { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:50:22', adjustedEnd: '0:50:28' }] },
          { id: 17, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }, { type: 'LowerBody', properties: { lowerBodyClothingLength: 'Long' } }],  instances: [{ adjustedStart: '0:58:14', adjustedEnd: '0:58:20' }] },
        ],
        detectedObjects: [
          ...Array.from({ length: 18 }, (_, i) => ({
            id: i + 1, type: 'Car', displayName: 'car', wikiDataId: 'Q1420', thumbnailId: null,
            instances: [{ confidence: 0.92 + (i % 5) * 0.01, adjustedStart: `0:${String(i * 3).padStart(2,'0')}:${String((i * 7) % 60).padStart(2,'0')}`, adjustedEnd: `0:${String(i * 3).padStart(2,'0')}:${String(((i * 7) % 60) + 3).padStart(2,'0')}` }]
          })),
          { id: 19, type: 'Bus', displayName: 'bus', wikiDataId: 'Q5638', thumbnailId: null,
            instances: [{ confidence: 0.912, adjustedStart: '0:21:14', adjustedEnd: '0:21:17' }, { confidence: 0.898, adjustedStart: '0:21:20', adjustedEnd: '0:21:22' }] },
          ...Array.from({ length: 24 }, (_, i) => ({
            id: i + 20, type: 'Car', displayName: 'car', wikiDataId: 'Q1420', thumbnailId: null,
            instances: [{ confidence: 0.93 + (i % 4) * 0.01, adjustedStart: `0:${String(22 + Math.floor(i * 1.6)).padStart(2,'0')}:00`, adjustedEnd: `0:${String(22 + Math.floor(i * 1.6)).padStart(2,'0')}:03` }]
          })),
        ],
        labels: ['outdoor', 'clothing', 'vehicle', 'person', 'man', 'car', 'text', 'land vehicle', 'wheel', 'road', 'tire', 'dog', 'transport', 'bicycle', 'footwear', 'bicycle wheel'],
        ocr: {
          fullText: 'Co',
          highlights: [{ text: 'Co', type: 'default', confidence: 0.914, timestamp: '0:37:06' }],
        },
        locations: [],
        audioEffects: [{ type: 'Silence', ratio: 1.0 }],
        keywords: ['surveillance', 'metro bank', 'vehicle', 'pedestrian', 'urban'],
      };
    }

    return {
      videoIndexerId: isMelbourneClip ? 'xlsrjmq93k' : 'demo-id',
      viAccountId: '3c5ee4a8-7bea-4501-b49c-52c0d533606e',
      viLocation: 'eastus2',
      processedAt: '2026-03-14T17:45:14Z',
      duration: isMelbourneClip ? '0:00:19' : '0:08:42',
      durationSeconds: isMelbourneClip ? 19 : 522,
      scenes: isMelbourneClip ? 1 : 2,
      shots: isMelbourneClip ? 1 : 4,
      source: 'video-indexer',
      faces: isMelbourneClip ? [
        { id: 1, name: 'Unknown #1', seenDuration: 3.7, firstSeen: '0:00:16', lastSeen: '0:00:19' },
        { id: 2, name: 'Unknown #2', seenDuration: 2.8, firstSeen: '0:00:12', lastSeen: '0:00:15' },
        { id: 3, name: 'Unknown #3', seenDuration: 0.7, firstSeen: '0:00:17', lastSeen: '0:00:18' },
      ] : [
        { id: 1, name: 'Person of Interest #1', seenDuration: 142, firstSeen: '0:02:18', lastSeen: '0:06:45' },
        { id: 2, name: 'Officer on Scene', seenDuration: 320, firstSeen: '0:00:00', lastSeen: '0:08:42' },
      ],
      observedPeople: isMelbourneClip ? [
        { id: 1, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } }], instances: [{ adjustedStart: '0:00:05', adjustedEnd: '0:00:12' }] },
        { id: 2, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Short' } }], instances: [{ adjustedStart: '0:00:08', adjustedEnd: '0:00:15' }] },
        { id: 3, thumbnailId: null, clothing: [{ type: 'UpperBody', properties: { upperBodyClothingLength: 'Long' } }], instances: [{ adjustedStart: '0:00:14', adjustedEnd: '0:00:19' }] },
      ] : [],
      detectedObjects: isMelbourneClip ? [
        { id: 1, type: 'Bus', displayName: 'Tram #6004', wikiDataId: 'Q5638', thumbnailId: null, instances: [{ confidence: 0.97, adjustedStart: '0:00:02', adjustedEnd: '0:00:18' }] },
        { id: 2, type: 'Car', displayName: 'Silver Top Taxi', wikiDataId: 'Q1420', thumbnailId: null, instances: [{ confidence: 0.94, adjustedStart: '0:00:05', adjustedEnd: '0:00:19' }] },
        { id: 3, type: 'Car', displayName: 'car', wikiDataId: 'Q1420', thumbnailId: null, instances: [{ confidence: 0.91, adjustedStart: '0:00:01', adjustedEnd: '0:00:08' }] },
      ] : [],
      labels: isMelbourneClip
        ? ['outdoor', 'land vehicle', 'vehicle', 'building', 'person', 'car', 'clothing', 'wheel', 'road', 'bus', 'transport', 'text']
        : ['outdoor', 'person', 'building', 'vehicle', 'road', 'uniform'],
      ocr: isMelbourneClip ? {
        fullText: 'YOUR NEXT GENERATION TRAM FOR MELBOURNE · Fleet 6004 · Route 122 · SILVER TOP TAXI 9966 · 131 008 · Southern Cross Station · Gloria J',
        highlights: [
          { text: 'Tram Fleet 6004', type: 'vehicle_id', confidence: 0.93 },
          { text: 'Route 122', type: 'route_number', confidence: 0.97 },
          { text: 'Silver Top Taxi 9966', type: 'vehicle_id', confidence: 0.94 },
          { text: '131 008', type: 'phone_number', confidence: 0.99 },
          { text: 'Southern Cross Station', type: 'location', confidence: 0.96 },
          { text: 'Gloria J', type: 'name', confidence: 0.91 },
        ],
      } : { fullText: '', highlights: [] },
      locations: isMelbourneClip ? ['Southern Cross Station, Melbourne VIC'] : [],
      audioEffects: [{ type: isMelbourneClip ? 'Silence' : 'Speech', ratio: isMelbourneClip ? 0.93 : 0.72 }],
      keywords: isMelbourneClip ? ['tram', 'taxi', 'melbourne', 'southern cross'] : [],
    };
  }

  async getDocumentInsights(evidenceId: string, fileName?: string): Promise<any> {
    try {
      const response = await this.client.get<{ success: boolean; data: any }>(`/evidence/${evidenceId}/document-insights`);
      return response.data.data;
    } catch {
      return this.getDemoDocumentInsights(fileName ?? '');
    }
  }

  private getDemoDocumentInsights(fileName: string): any {
    const isCriminalHistory = fileName.toLowerCase().includes('locklear') || fileName.toLowerCase().includes('criminal_history') || fileName === 'EVD-2026-0019';
    if (!isCriminalHistory) return null;
    return {
      documentType: 'Criminal History Report',
      source: 'Maryland CJIS – Identification/Index System',
      reportDate: '08/11/2025',
      cbfNumber: 'BIN2507784BC',
      processedAt: '2026-03-12T08:30:00Z',
      confidence: 0.97,
      subject: {
        fullName: 'LOCKLEAR, KENNETH LEE',
        firstName: 'Kenneth',
        lastName: 'Locklear',
        middleName: 'Lee',
        fbiNumber: 'LXPXVELNM',
        state: 'MD',
        cautionFlags: ['DANGEROUS', 'DANGEROUS/RESISTS', 'APPROACH WITH CAUTION'],
      },
      chargeSummary: {
        totalCharges: 9,
        states: ['MD'],
        oldestCharge: '03/02/2017',
        mostRecentCharge: '01/04/2025',
      },
      charges: [
        {
          date: '03/02/2017',
          state: 'MD',
          agency: 'Anne Arundel Cnty PD',
          chargeCode: 'CR-10201',
          charge: 'Disorderly Conduct',
          disposition: 'Probation Before Judgment / Fined',
        },
        {
          date: '03/16/2018',
          state: 'MD',
          agency: 'Baltimore City PD',
          chargeCode: 'CR-3203',
          charge: 'Assault – 2nd Degree',
          disposition: 'Released to Another Agency',
        },
        {
          date: '03/27/2018',
          state: 'MD',
          agency: 'Baltimore City PD',
          chargeCode: 'CR-3203',
          charge: 'Assault – 2nd Degree',
          disposition: 'Released to Another Agency',
        },
        {
          date: '08/26/2018',
          state: 'MD',
          agency: 'Baltimore Cnty PD Dundalk',
          chargeCode: 'CR-6403',
          charge: 'Trespass: Private Property (VOP)',
          disposition: 'Nolle Prosequi',
        },
        {
          date: '10/28/2022',
          state: 'MD',
          agency: 'Baltimore Cnty PD Dundalk',
          chargeCode: 'CR-3203',
          charge: 'Assault – 2nd Degree',
          disposition: 'Nolle Prosequi',
        },
        {
          date: '01/24/2023',
          state: 'MD',
          agency: 'Baltimore Cnty PD Dundalk',
          chargeCode: 'CR-10201',
          charge: 'Disorderly Conduct',
          disposition: 'Turned Over to Commissioner',
        },
        {
          date: '01/24/2023',
          state: 'MD',
          agency: 'Baltimore Cnty PD Dundalk',
          chargeCode: 'CR-10201',
          charge: 'Fail to Obey Reasonable / Lawful Order',
          disposition: 'Pending',
        },
        {
          date: '10/07/2023',
          state: 'MD',
          agency: 'Baltimore Cnty PD Dundalk',
          chargeCode: '—',
          charge: 'Assault / Related Offenses',
          disposition: 'Consolidated with Prior Arrest',
        },
        {
          date: '01/04/2025',
          state: 'MD',
          agency: 'Baltimore County Detention',
          chargeCode: '—',
          charge: 'Booking / Fingerprinted',
          disposition: 'No Court Data Reported',
        },
      ],
    };
  }

  /**
   * Get base URL for SignalR connection
   */
  getBaseUrl(): string {
    return this.client.defaults.baseURL || '/api';
  }

  /**
   * Demo notifications for UI development
   */
  private getDemoNotifications(): any[] {
    const now = new Date();
    return [
      {
        id: 'notif-1',
        type: 'EVIDENCE_UPLOADED',
        category: 'Evidence',
        priority: 'Medium',
        title: 'Evidence Upload Confirmed',
        body: 'Body camera footage (BC-2024-001.mp4) has been uploaded and verified. SHA-256 hash recorded in custody chain.',
        evidenceId: 'ev-001',
        caseNumber: 'CR-2024-00892',
        actionUrl: '/cases/CR-2024-00892',
        actionLabel: 'View Case',
        createdAt: new Date(now.getTime() - 5 * 60000).toISOString(),
        state: 'Read',
        triggeredBy: { userId: 'user-1', name: 'Det. James Chen', role: 'LawEnforcement' },
      },
      {
        id: 'notif-2',
        type: 'SUBMISSION_RECEIVED',
        category: 'Evidence',
        priority: 'High',
        title: 'New Public Submission',
        body: 'A new evidence submission requires your review. Submitted by: citizen@example.com',
        caseNumber: 'CR-2024-00893',
        actionUrl: '/submissions',
        actionLabel: 'Review Submission',
        createdAt: new Date(now.getTime() - 15 * 60000).toISOString(),
        state: 'Read',
      },
      {
        id: 'notif-3',
        type: 'LEGAL_HOLD_APPLIED',
        category: 'LegalHold',
        priority: 'Critical',
        title: 'Legal Hold Applied',
        body: 'A legal hold has been applied to evidence in case CR-2024-00892 per court order #2024-LH-001. Acknowledgment required.',
        caseNumber: 'CR-2024-00892',
        actionUrl: '/disposition',
        actionLabel: 'View Legal Hold',
        createdAt: new Date(now.getTime() - 30 * 60000).toISOString(),
        state: 'Acknowledged',
        expiresAt: new Date(now.getTime() + 24 * 60 * 60000).toISOString(),
      },
      {
        id: 'notif-4',
        type: 'DISCOVERY_REQUEST_RECEIVED',
        category: 'Discovery',
        priority: 'High',
        title: 'Discovery Request',
        body: 'Attorney Sarah Mitchell has requested access to evidence in case CR-2024-00892.',
        caseNumber: 'CR-2024-00892',
        actionUrl: '/discovery',
        actionLabel: 'Review Request',
        createdAt: new Date(now.getTime() - 60 * 60000).toISOString(),
        state: 'Read',
      },
      {
        id: 'notif-5',
        type: 'CASE_ASSIGNED',
        category: 'Case',
        priority: 'Medium',
        title: 'Case Assignment',
        body: 'You have been assigned to case CR-2024-00895: State v. Williams.',
        caseNumber: 'CR-2024-00895',
        actionUrl: '/cases/CR-2024-00895',
        actionLabel: 'View Case',
        createdAt: new Date(now.getTime() - 2 * 60 * 60000).toISOString(),
        state: 'Acknowledged',
      },
    ];
  }

  async resetDemoData(): Promise<{ success: boolean; message: string; counts?: { cases: number; evidence: number; custodyEvents: number } }> {
    const response = await this.client.post<{ success: boolean; message: string; counts?: { cases: number; evidence: number; custodyEvents: number } }>('/demo/reset');
    return response.data;
  }

  async extractAllEvidence(): Promise<{ success: boolean; results: { total: number; processed: number; failed: number; details: Array<{ id: string; title: string; status: string }> } }> {
    const response = await this.client.post<{ success: boolean; results: { total: number; processed: number; failed: number; details: Array<{ id: string; title: string; status: string }> } }>('/evidence/extract-all');
    return response.data;
  }

  async extractChargeHistory(evidenceId: string, currentArrestDate?: string): Promise<import('../components/ChargeHistoryTable').ChargeHistoryData> {
    const response = await this.client.post<{ success: boolean; data: import('../components/ChargeHistoryTable').ChargeHistoryData }>(
      `/evidence/${evidenceId}/charge-history`,
      currentArrestDate ? { currentArrestDate } : {}
    );
    if (!response.data.success) throw new Error('Charge history extraction failed');
    return response.data.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
